import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { authApi } from '../../helpers/auth';

const PAGE_LIST = [
  { slug: 'decouvrir', label: 'Découvrir le MACTe' },
  { slug: 'visiter', label: 'Visiter le MACTe' },
  { slug: 'politique-confidentialite', label: 'Politique de confidentialité' },
  { slug: 'mentions-legales', label: 'Mentions légales' },
  { slug: 'projet-p1', label: 'Projet Racines en mouvement' },
  { slug: 'projet-p4', label: 'Projet Mémorial Vision' },
  { slug: 'restaurant', label: 'Restaurant Ichali' },
];

const AdminPages = () => {
  const [selectedPage, setSelectedPage] = useState(PAGE_LIST[0].slug);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadPage(selectedPage);
  }, [selectedPage]);

  const loadPage = async (slug) => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/pages.php?page=${slug}`);
      if (!response.ok) throw new Error('Erreur de chargement');
      const data = await response.json();
      setPageData(data);
    } catch (err) {
      setError('Erreur lors du chargement');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      const token = authApi.getToken();
      const response = await fetch(`/api/pages.php?page=${selectedPage}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(pageData),
      });
      if (!response.ok) throw new Error('Erreur de sauvegarde');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Erreur lors de la sauvegarde');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const updateNested = (path, value) => {
    setPageData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        const key = isNaN(keys[i]) ? keys[i] : parseInt(keys[i]);
        obj = obj[key];
      }
      const lastKey = isNaN(keys[keys.length - 1]) ? keys[keys.length - 1] : parseInt(keys[keys.length - 1]);
      obj[lastKey] = value;
      return updated;
    });
  };

  const addSection = () => {
    setPageData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated.sections.push({ title: '', content: '' });
      return updated;
    });
  };

  const removeSection = (index) => {
    setPageData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated.sections.splice(index, 1);
      return updated;
    });
  };

  const moveSection = (index, direction) => {
    setPageData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= updated.sections.length) return prev;
      const temp = updated.sections[index];
      updated.sections[index] = updated.sections[newIndex];
      updated.sections[newIndex] = temp;
      return updated;
    });
  };

  const addListItem = (sectionIndex, field) => {
    setPageData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      if (!updated.sections[sectionIndex][field]) {
        updated.sections[sectionIndex][field] = [];
      }
      updated.sections[sectionIndex][field].push('');
      return updated;
    });
  };

  const removeListItem = (sectionIndex, field, itemIndex) => {
    setPageData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated.sections[sectionIndex][field].splice(itemIndex, 1);
      return updated;
    });
  };

  // Generic add/remove for nested arrays (e.g. smartVisit.features)
  const addNestedItem = (path, item) => {
    setPageData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      let obj = updated;
      for (const key of path.split('.')) obj = obj[key];
      obj.push(item);
      return updated;
    });
  };

  const removeNestedItem = (path, index) => {
    setPageData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      let obj = updated;
      for (const key of path.split('.')) obj = obj[key];
      obj.splice(index, 1);
      return updated;
    });
  };

  // For visiter page tarifs
  const addTarif = () => {
    setPageData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated.tarifs.push({ title: '', price: '' });
      return updated;
    });
  };

  const removeTarif = (index) => {
    setPageData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated.tarifs.splice(index, 1);
      return updated;
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  const renderHero = () => {
    if (!pageData?.hero) return null;
    return (
      <div className="card mb-4">
        <div className="card-header"><h5 className="mb-0">Hero</h5></div>
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">Titre</label>
            <input type="text" className="form-control" value={pageData.hero.title || ''} onChange={e => updateNested('hero.title', e.target.value)} />
          </div>
          <div className="mb-3">
            <label className="form-label">Sous-titre</label>
            <textarea className="form-control" rows={2} value={pageData.hero.subtitle || ''} onChange={e => updateNested('hero.subtitle', e.target.value)} />
          </div>
          {pageData.hero.buttonText !== undefined && (
            <div>
              <label className="form-label">Texte du bouton</label>
              <input type="text" className="form-control" value={pageData.hero.buttonText || ''} onChange={e => updateNested('hero.buttonText', e.target.value)} />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderInfos = () => {
    if (!pageData?.infos) return null;
    return (
      <div className="card mb-4">
        <div className="card-header"><h5 className="mb-0">Informations pratiques</h5></div>
        <div className="card-body">
          {Object.entries(pageData.infos).map(([key, value]) => (
            <div className="mb-3" key={key}>
              <label className="form-label text-capitalize">{key}</label>
              <input type="text" className="form-control" value={value} onChange={e => updateNested(`infos.${key}`, e.target.value)} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTarifs = () => {
    if (!pageData?.tarifs) return null;
    return (
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Tarifs</h5>
          <button className="btn btn-sm btn-outline-primary" onClick={addTarif}>
            <Plus size={16} className="me-1" /> Ajouter
          </button>
        </div>
        <div className="card-body">
          {pageData.tarifs.map((tarif, index) => (
            <div key={index} className="border rounded p-2 mb-2">
              <div className="row g-2 align-items-center">
                <div className="col">
                  <input type="text" className="form-control form-control-sm" placeholder="Intitulé" value={tarif.title} onChange={e => updateNested(`tarifs.${index}.title`, e.target.value)} />
                </div>
                <div className="col-3">
                  <input type="text" className="form-control form-control-sm" placeholder="Prix" value={tarif.price} onChange={e => updateNested(`tarifs.${index}.price`, e.target.value)} />
                </div>
                <div className="col-auto">
                  <button className="btn btn-sm btn-outline-danger" onClick={() => removeTarif(index)}><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="mt-1">
                <input type="text" className="form-control form-control-sm" placeholder="Note (optionnelle)" value={tarif.note || ''} onChange={e => updateNested(`tarifs.${index}.note`, e.target.value)} />
              </div>
              {Array.isArray(tarif.sublist) && (
                <div className="mt-2 ps-3 border-start">
                  <input type="text" className="form-control form-control-sm mb-1" placeholder="Titre de la sous-liste" value={tarif.sublistTitle || ''} onChange={e => updateNested(`tarifs.${index}.sublistTitle`, e.target.value)} />
                  {tarif.sublist.map((subItem, subIndex) => (
                    <div key={subIndex} className="d-flex gap-1 mb-1">
                      <input type="text" className="form-control form-control-sm" value={subItem} onChange={e => updateNested(`tarifs.${index}.sublist.${subIndex}`, e.target.value)} />
                      <button className="btn btn-sm btn-outline-danger py-0 px-1" onClick={() => removeNestedItem(`tarifs.${index}.sublist`, subIndex)}><Trash2 size={12} /></button>
                    </div>
                  ))}
                  <button className="btn btn-sm btn-outline-secondary py-0 px-1" onClick={() => addNestedItem(`tarifs.${index}.sublist`, '')}>
                    <Plus size={12} /> Ajouter un élément
                  </button>
                </div>
              )}
            </div>
          ))}
          {pageData.tarifsNote !== undefined && (
            <div className="mt-3">
              <label className="form-label">Note tarifs</label>
              <input type="text" className="form-control" value={pageData.tarifsNote} onChange={e => updateNested('tarifsNote', e.target.value)} />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSmartVisit = () => {
    const sv = pageData?.smartVisit;
    if (!sv) return null;
    return (
      <div className="card mb-4">
        <div className="card-header"><h5 className="mb-0">Smart Visit</h5></div>
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">Badge</label>
            <input type="text" className="form-control" value={sv.badge || ''} onChange={e => updateNested('smartVisit.badge', e.target.value)} />
          </div>
          <div className="mb-3">
            <label className="form-label">Titre</label>
            <input type="text" className="form-control" value={sv.title || ''} onChange={e => updateNested('smartVisit.title', e.target.value)} />
          </div>
          <div className="mb-3">
            <label className="form-label">Image (chemin)</label>
            <input type="text" className="form-control" value={sv.image || ''} onChange={e => updateNested('smartVisit.image', e.target.value)} />
          </div>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="form-label fw-bold mb-0">Paragraphes d'introduction</label>
              <button className="btn btn-sm btn-outline-secondary py-0 px-1" onClick={() => addNestedItem('smartVisit.intro', '')}>
                <Plus size={12} />
              </button>
            </div>
            {(sv.intro || []).map((p, i) => (
              <div key={i} className="d-flex gap-1 mb-1">
                <textarea className="form-control form-control-sm" rows={2} value={p} onChange={e => updateNested(`smartVisit.intro.${i}`, e.target.value)} />
                <button className="btn btn-sm btn-outline-danger py-0 px-1" onClick={() => removeNestedItem('smartVisit.intro', i)}><Trash2 size={12} /></button>
              </div>
            ))}
          </div>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="form-label fw-bold mb-0">Points forts (titre + texte)</label>
              <button className="btn btn-sm btn-outline-secondary py-0 px-1" onClick={() => addNestedItem('smartVisit.highlights', { title: '', text: '' })}>
                <Plus size={12} />
              </button>
            </div>
            {(sv.highlights || []).map((h, i) => (
              <div key={i} className="row g-2 mb-2 align-items-center">
                <div className="col-4">
                  <input type="text" className="form-control form-control-sm" placeholder="Titre" value={h.title} onChange={e => updateNested(`smartVisit.highlights.${i}.title`, e.target.value)} />
                </div>
                <div className="col">
                  <input type="text" className="form-control form-control-sm" placeholder="Texte" value={h.text} onChange={e => updateNested(`smartVisit.highlights.${i}.text`, e.target.value)} />
                </div>
                <div className="col-auto">
                  <button className="btn btn-sm btn-outline-danger py-0 px-1" onClick={() => removeNestedItem('smartVisit.highlights', i)}><Trash2 size={12} /></button>
                </div>
              </div>
            ))}
          </div>

          <div className="mb-1">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="form-label fw-bold mb-0">Liste des avantages</label>
              <button className="btn btn-sm btn-outline-secondary py-0 px-1" onClick={() => addNestedItem('smartVisit.features', '')}>
                <Plus size={12} />
              </button>
            </div>
            {(sv.features || []).map((f, i) => (
              <div key={i} className="d-flex gap-1 mb-1">
                <input type="text" className="form-control form-control-sm" value={f} onChange={e => updateNested(`smartVisit.features.${i}`, e.target.value)} />
                <button className="btn btn-sm btn-outline-danger py-0 px-1" onClick={() => removeNestedItem('smartVisit.features', i)}><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderSectionField = (section, sectionIndex, field) => {
    if (section[field] === undefined) return null;

    if (field === 'title') {
      return (
        <div className="mb-2">
          <label className="form-label small fw-bold">Titre</label>
          <input type="text" className="form-control" value={section.title || ''} onChange={e => updateNested(`sections.${sectionIndex}.title`, e.target.value)} />
        </div>
      );
    }

    if (field === 'content') {
      return (
        <div className="mb-2">
          <label className="form-label small fw-bold">Contenu</label>
          <textarea className="form-control" rows={3} value={section.content || ''} onChange={e => updateNested(`sections.${sectionIndex}.content`, e.target.value)} />
        </div>
      );
    }

    if (field === 'blockquote') {
      return (
        <div className="mb-2">
          <label className="form-label small fw-bold">Citation</label>
          <textarea className="form-control" rows={2} value={section.blockquote || ''} onChange={e => updateNested(`sections.${sectionIndex}.blockquote`, e.target.value)} />
        </div>
      );
    }

    if (field === 'blockquoteAuthor') {
      return (
        <div className="mb-2">
          <label className="form-label small fw-bold">Auteur de la citation</label>
          <input type="text" className="form-control" value={section.blockquoteAuthor || ''} onChange={e => updateNested(`sections.${sectionIndex}.blockquoteAuthor`, e.target.value)} />
        </div>
      );
    }

    if (field === 'footer') {
      return (
        <div className="mb-2">
          <label className="form-label small fw-bold">Note de bas de section</label>
          <input type="text" className="form-control" value={section.footer || ''} onChange={e => updateNested(`sections.${sectionIndex}.footer`, e.target.value)} />
        </div>
      );
    }

    if (field === 'paragraphs' && Array.isArray(section.paragraphs)) {
      return (
        <div className="mb-2">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <label className="form-label small fw-bold mb-0">Paragraphes</label>
            <button className="btn btn-sm btn-outline-secondary py-0 px-1" onClick={() => addListItem(sectionIndex, 'paragraphs')}>
              <Plus size={12} />
            </button>
          </div>
          {section.paragraphs.map((p, i) => (
            <div key={i} className="d-flex gap-1 mb-1">
              <textarea className="form-control form-control-sm" rows={2} value={p} onChange={e => updateNested(`sections.${sectionIndex}.paragraphs.${i}`, e.target.value)} />
              <button className="btn btn-sm btn-outline-danger py-0 px-1" onClick={() => removeListItem(sectionIndex, 'paragraphs', i)}><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      );
    }

    if (field === 'list' && Array.isArray(section.list)) {
      return (
        <div className="mb-2">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <label className="form-label small fw-bold mb-0">Liste</label>
            <button className="btn btn-sm btn-outline-secondary py-0 px-1" onClick={() => addListItem(sectionIndex, 'list')}>
              <Plus size={12} />
            </button>
          </div>
          {section.list.map((item, i) => (
            <div key={i} className="d-flex gap-1 mb-1">
              <input type="text" className="form-control form-control-sm" value={item} onChange={e => updateNested(`sections.${sectionIndex}.list.${i}`, e.target.value)} />
              <button className="btn btn-sm btn-outline-danger py-0 px-1" onClick={() => removeListItem(sectionIndex, 'list', i)}><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      );
    }

    if (field === 'profiles' && Array.isArray(section.profiles)) {
      return (
        <div className="mb-2">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <label className="form-label small fw-bold mb-0">Portraits</label>
            <button className="btn btn-sm btn-outline-secondary py-0 px-1" onClick={() => addNestedItem(`sections.${sectionIndex}.profiles`, { name: '', bio: '' })}>
              <Plus size={12} />
            </button>
          </div>
          {section.profiles.map((profile, i) => (
            <div key={i} className="border rounded p-2 mb-1">
              <div className="d-flex gap-1 mb-1">
                <input type="text" className="form-control form-control-sm" placeholder="Nom" value={profile.name || ''} onChange={e => updateNested(`sections.${sectionIndex}.profiles.${i}.name`, e.target.value)} />
                <button className="btn btn-sm btn-outline-danger py-0 px-1" onClick={() => removeNestedItem(`sections.${sectionIndex}.profiles`, i)}><Trash2 size={12} /></button>
              </div>
              <textarea className="form-control form-control-sm" rows={4} placeholder="Texte" value={profile.bio || ''} onChange={e => updateNested(`sections.${sectionIndex}.profiles.${i}.bio`, e.target.value)} />
            </div>
          ))}
        </div>
      );
    }

    if (field === 'lines' && Array.isArray(section.lines)) {
      return (
        <div className="mb-2">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <label className="form-label small fw-bold mb-0">Lignes</label>
            <button className="btn btn-sm btn-outline-secondary py-0 px-1" onClick={() => addListItem(sectionIndex, 'lines')}>
              <Plus size={12} />
            </button>
          </div>
          {section.lines.map((line, i) => (
            <div key={i} className="d-flex gap-1 mb-1">
              <input type="text" className="form-control form-control-sm" value={line} onChange={e => updateNested(`sections.${sectionIndex}.lines.${i}`, e.target.value)} />
              <button className="btn btn-sm btn-outline-danger py-0 px-1" onClick={() => removeListItem(sectionIndex, 'lines', i)}><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  const renderSections = () => {
    if (!pageData?.sections) return null;
    // Fields to render in order, skipping internal ones
    const skipFields = ['blockquoteStyle', 'link'];
    const fieldOrder = ['title', 'content', 'paragraphs', 'list', 'profiles', 'lines', 'blockquote', 'blockquoteAuthor', 'footer'];

    return (
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Sections</h5>
          <button className="btn btn-sm btn-outline-primary" onClick={addSection}>
            <Plus size={16} className="me-1" /> Ajouter une section
          </button>
        </div>
        <div className="card-body">
          {pageData.sections.map((section, index) => (
            <div key={index} className="border rounded p-3 mb-3 bg-light">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="badge bg-secondary">Section {index + 1}</span>
                <div className="d-flex gap-1">
                  <button className="btn btn-sm btn-outline-secondary py-0 px-1" onClick={() => moveSection(index, -1)} disabled={index === 0}>
                    <ChevronUp size={14} />
                  </button>
                  <button className="btn btn-sm btn-outline-secondary py-0 px-1" onClick={() => moveSection(index, 1)} disabled={index === pageData.sections.length - 1}>
                    <ChevronDown size={14} />
                  </button>
                  <button className="btn btn-sm btn-outline-danger py-0 px-1" onClick={() => removeSection(index)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {fieldOrder.map(field => {
                if (skipFields.includes(field)) return null;
                return <React.Fragment key={field}>{renderSectionField(section, index, field)}</React.Fragment>;
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderExtraFields = () => {
    // Render top-level simple string fields that aren't hero/sections/infos/tarifs
    const skip = ['hero', 'sections', 'infos', 'tarifs', 'tarifsNote'];
    if (!pageData) return null;
    const extras = Object.entries(pageData).filter(([key]) => !skip.includes(key) && typeof pageData[key] === 'string');
    if (extras.length === 0) return null;

    return (
      <div className="card mb-4">
        <div className="card-header"><h5 className="mb-0">Autres champs</h5></div>
        <div className="card-body">
          {extras.map(([key, value]) => (
            <div className="mb-3" key={key}>
              <label className="form-label">{key}</label>
              <input type="text" className="form-control" value={value} onChange={e => updateNested(key, e.target.value)} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Pages statiques</h2>
        <div className="d-flex align-items-center gap-2">
          {success && <span className="text-success">Sauvegardé !</span>}
          {error && <span className="text-danger">{error}</span>}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !pageData}>
            <Save size={16} className="me-2" />
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      {/* Page selector */}
      <div className="mb-4">
        <div className="d-flex flex-wrap gap-2">
          {PAGE_LIST.map(p => (
            <button
              key={p.slug}
              className={`btn ${selectedPage === p.slug ? 'btn-dark' : 'btn-outline-dark'}`}
              onClick={() => setSelectedPage(p.slug)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {pageData && (
        <>
          {renderHero()}
          {renderInfos()}
          {renderTarifs()}
          {renderSmartVisit()}
          {renderSections()}
          {renderExtraFields()}
        </>
      )}
    </div>
  );
};

export default AdminPages;
